<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  activity: {
    type: Object,
    required: true,
  },
});

const isExpanded = ref(false);

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

const ACTIVITY_TYPE_CONFIG = {
  conversion: { icon: 'i-lucide-target', color: 'text-green-500' },
  form_submission: { icon: 'i-lucide-file-text', color: 'text-blue-500' },
  page_view: { icon: 'i-lucide-eye', color: 'text-n-slate-10' },
  email_open: { icon: 'i-lucide-mail-open', color: 'text-yellow-500' },
  deal_stage_change: { icon: 'i-lucide-git-branch', color: 'text-purple-500' },
  deal_created: { icon: 'i-lucide-plus-circle', color: 'text-green-500' },
  deal_won: { icon: 'i-lucide-trophy', color: 'text-yellow-600' },
  deal_lost: { icon: 'i-lucide-x-circle', color: 'text-red-500' },
  contact_created: { icon: 'i-lucide-user-plus', color: 'text-blue-500' },
  contact_lifecycle_change: {
    icon: 'i-lucide-refresh-cw',
    color: 'text-orange-500',
  },
  note_added: { icon: 'i-lucide-sticky-note', color: 'text-n-slate-10' },
  task_created: { icon: 'i-lucide-check-square', color: 'text-blue-500' },
  custom: { icon: 'i-lucide-zap', color: 'text-n-slate-10' },
};

const activityConfig = computed(() => {
  return (
    ACTIVITY_TYPE_CONFIG[props.activity.activity_type] ||
    ACTIVITY_TYPE_CONFIG.custom
  );
});

const hasProperties = computed(() => {
  const p = props.activity.properties;
  return p && typeof p === 'object' && Object.keys(p).length > 0;
});

const propertiesEntries = computed(() => {
  const p = props.activity.properties;
  if (!p || typeof p !== 'object') return [];
  return Object.entries(p).filter(
    ([key]) => key !== 'inherited_from_contact_id'
  );
});

const isInherited = computed(() => {
  const p = props.activity.properties;
  return p && p.inherited_from_contact_id;
});

const sourceLabel = computed(() => {
  const source = props.activity.source;
  if (!source) return '';
  const labels = {
    api: 'API',
    web: 'Web',
    sdk: 'SDK',
    import: 'Importação',
    system: 'Sistema',
    manual: 'Manual',
    webhook: 'Webhook',
  };
  return labels[source] || source;
});

const relativeTime = computed(() => {
  const date = props.activity.occurred_at || props.activity.created_at;
  if (!date) return '';
  return formatRelativeTime(new Date(date));
});

const fullDate = computed(() => {
  const date = props.activity.occurred_at || props.activity.created_at;
  if (!date) return '';
  try {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return date;
  }
});

const formatRelativeTime = date => {
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);

  if (diffSec < 60) return 'agora';
  if (diffMin < 60) return `${diffMin}min atras`;
  if (diffHour < 24) return `${diffHour}h atras`;
  if (diffDay === 1) return 'ontem';
  if (diffDay < 7) return `${diffDay}d atras`;
  if (diffWeek < 5) return `${diffWeek}sem atras`;
  if (diffMonth < 12) return `${diffMonth}m atras`;
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatPropertyValue = value => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Nao';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};
</script>

<template>
  <div
    class="group flex flex-col border-b border-n-weak last:border-b-0 transition-colors duration-100"
  >
    <!-- Main row -->
    <button
      class="flex items-start gap-2.5 w-full py-2.5 px-3 text-left border-0 bg-transparent cursor-pointer hover:bg-n-solid-2 transition-colors duration-100 rounded-md"
      :class="{ 'bg-n-solid-2': isExpanded }"
      @click="toggleExpanded"
    >
      <!-- Icon -->
      <div class="flex-shrink-0 mt-0.5">
        <span
          :class="[activityConfig.icon, activityConfig.color]"
          class="size-4"
        />
      </div>

      <!-- Content -->
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-1.5 flex-wrap">
          <span class="text-sm font-medium text-n-slate-12 leading-tight">
            {{ activity.activity_name }}
          </span>
          <span
            v-if="isInherited"
            class="text-[10px] font-medium px-1.5 py-0.5 rounded bg-n-solid-3 text-n-slate-10 whitespace-nowrap"
          >
            Herdado do contato
          </span>
        </div>
        <div class="flex items-center gap-1.5 mt-0.5">
          <span
            v-if="sourceLabel"
            class="text-xs text-n-slate-10"
          >
            {{ sourceLabel }}
          </span>
          <span
            v-if="sourceLabel && relativeTime"
            class="text-n-slate-8"
          >
            &middot;
          </span>
          <span
            v-if="relativeTime"
            v-tooltip.top="fullDate"
            class="text-xs text-n-slate-9"
          >
            {{ relativeTime }}
          </span>
        </div>
      </div>

      <!-- Expand arrow -->
      <div
        v-if="hasProperties"
        class="flex-shrink-0 mt-1"
      >
        <span
          class="i-lucide-chevron-down size-3.5 text-n-slate-9 transition-transform duration-200"
          :class="{ 'rotate-180': isExpanded }"
        />
      </div>
    </button>

    <!-- Expanded properties -->
    <div
      v-if="isExpanded && hasProperties"
      class="ml-9 mr-3 mb-2.5 rounded-md bg-n-solid-2 border border-n-weak"
    >
      <div class="divide-y divide-n-weak">
        <div
          v-for="([key, value], index) in propertiesEntries"
          :key="index"
          class="flex items-start gap-2 px-3 py-1.5"
        >
          <span class="text-xs text-n-slate-10 font-medium min-w-[80px] flex-shrink-0 break-all">
            {{ key }}
          </span>
          <span class="text-xs text-n-slate-12 break-all">
            {{ formatPropertyValue(value) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
